import React, { useState, useMemo, useEffect } from 'react';
import { systemUsers as usersApi, systemRoles as rolesApi } from '../../api/endpoints';
import { useFetchList, useSearch, usePagination, useNotification } from '../../hooks';
import useStyles from '../../theme/useStyles';
import { nextSequence } from '../../utils/records';
import { isAdmin } from '../../utils/auth';
import {
  PageHeader, SearchBar, Pagination, Notification, PromptDialog, FormLayout, Input, Select, Badge, Button, TablePanel
} from '../../components/ui';
import PermissionGrid, { noPermissions, parsePermissions } from './PermissionGrid';
import RolesManager from './RolesManager';
import type { ParsedSystemUser, SystemRole } from '../../types';
import type { Column } from '../../components/ui';

const MASTER_ACCOUNT = 'admin';

const EMPTY_FORM = { name: '', username: '', password: '', userRole: '', roleId: '' };

const UserAccessManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [viewMode, setViewMode] = useState('history');
  const [userForm, setUserForm] = useState(EMPTY_FORM);
  const [permissions, setPermissions] = useState(noPermissions);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [disablingUser, setDisablingUser] = useState(null);

  // The roles a user can hold (Settings › Users › Roles).
  const [roles, setRoles] = useState<SystemRole[]>([]);
  const loadRoles = () => rolesApi.list()
    .then((list) => setRoles(Array.isArray(list) ? list : []))
    .catch(() => setRoles([]));
  useEffect(() => { loadRoles(); }, []);

  const isEditing = editingId !== null;
  const { notification, notify, dismiss } = useNotification();
  const { data: rawUsers, setData: setRawUsers, loading, refetch } =
    useFetchList((p, o) => usersApi.list(p, o));

  // The backend's `recordId` is the human-facing ID; `id` is the row key.
  // Permissions arrive as a JSON string.
  const { active, disabled } = useMemo(() => {
    const mapped = rawUsers.map((u) => {
      let parsed = {};
      try { parsed = u.permissions ? JSON.parse(u.permissions) : {}; } catch { parsed = {}; }
      return { ...u, rawId: u.id, parsedPermissions: parsed };
    });

    return {
      active: mapped.filter((u) => u.status !== 'VOID').sort((a, b) => b.rawId - a.rawId),
      disabled: mapped
        .filter((u) => u.status === 'VOID')
        .sort((a, b) =>
          new Date(b.deletedAt || 0).getTime() - new Date(a.deletedAt || 0).getTime())
    };
  }, [rawUsers]);

  const isDeletedView = viewMode === 'deleted';
  const dataset = isDeletedView ? disabled : active;

  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    dataset, ['name', 'username', 'userRole', 'recordId']
  );
  const page = usePagination(filtered);

  const resetForm = () => {
    setUserForm(EMPTY_FORM);
    setPermissions(noPermissions());
    setEditingId(null);
  };

  const switchView = (key) => {
    setViewMode(key);
    resetForm();
    page.reset();
    clear();
  };

  const chosenRole = roles.find((r) => String(r.id) === String(userForm.roleId));
  // Active roles, plus the one this user already holds even if turned off.
  const roleOptions = roles
    .filter((r) => r.active || String(r.id) === String(userForm.roleId))
    .map((r) => ({ value: String(r.id), label: r.active ? r.name : `${r.name} (turned off)` }));

  const chooseRole = (value: string) => {
    const role = roles.find((r) => String(r.id) === value);
    setUserForm((prev) => ({ ...prev, roleId: value, userRole: role?.name || '' }));
    // A new user starts from the role's default permissions, when it has some.
    if (!isEditing && role?.defaultPermissions) setPermissions(parsePermissions(role.defaultPermissions));
  };

  const handleEdit = (user) => {
    setUserForm({
      name: user.name || '',
      username: user.username || '',
      // Left blank on edit: the API never returns the stored hash, and a blank
      // means "keep the current password" (CHM-106). Showing user.password only
      // ever put an empty required box on screen.
      password: '',
      userRole: user.userRole || '',
      roleId: user.roleId != null ? String(user.roleId) : ''
    });
    setPermissions({ ...noPermissions(), ...user.parsedPermissions });
    setEditingId(user.rawId);
    setViewMode('add');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const userRole = userForm.userRole.trim();
    if (!userForm.roleId) { notify('System Role is required.', 'error'); return; }

    const duplicate = active.some(
      (u) => u.username?.toLowerCase() === userForm.username.toLowerCase() && u.rawId !== editingId
    );
    if (duplicate) { notify('This username is already taken.', 'error'); return; }

    const existing = isEditing ? active.find((u) => u.rawId === editingId) : null;
    // On edit, a blank password must not overwrite the stored hash — drop the key
    // entirely so the server keeps the existing one.
    const { password, ...formNoPass } = userForm;
    const payload = {
      ...(isEditing && !password.trim() ? formNoPass : userForm),
      userRole,
      roleId: Number(userForm.roleId),
      permissions: JSON.stringify(permissions),
      recordId: existing?.recordId || nextSequence(rawUsers as unknown as Array<Record<string, unknown>>, 'USR'),
      status: 'ACTIVE' as const
    };

    setSaving(true);
    try {
      if (isEditing) await usersApi.update(editingId, payload);
      else await usersApi.create(payload);
      await refetch();
      notify(isEditing ? 'User profile & permissions updated.' : 'New user created successfully.', 'success');
      resetForm();
      setViewMode('history');
    } catch (err) {
      notify(err.message || 'Could not save the user.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const requestDisable = (user) => {
    if (user.username === MASTER_ACCOUNT) {
      notify('The master admin account cannot be voided.', 'error');
      return;
    }
    setDisablingUser(user);
  };

  const confirmDisable = async (reason) => {
    const user = disablingUser;
    setDisablingUser(null);
    // Guard: the dialog can only reach here with a row selected, but the
    // state is nulled before the await so TypeScript cannot prove it.
    if (!user) return;
    try {
      const payload = {
        ...user,
        id: user.rawId,
        status: 'VOID',
        deleteReason: reason,
        deletedAt: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      };
      await usersApi.update(user.rawId, payload);
      setRawUsers((prev) => prev.map((u) => (u.id === user.rawId ? { ...u, ...payload } : u)));
      notify('User account disabled securely.', 'success');
    } catch (err) {
      notify(err.message || 'Could not disable the account.', 'error');
    }
  };

  const baseColumns: Array<Column<ParsedSystemUser>> = [
    { key: 'recordId', header: 'Record ID', nowrap: true, render: (u) => <strong>{u.recordId}</strong> },
    {
      key: 'name',
      header: 'Name & System Role',
      render: (u) => (
        <>
          <strong>{u.name}</strong><br />
          <Badge tone={isAdmin(u) ? 'error' : 'accent'}>
            {u.userRole}
          </Badge>
        </>
      )
    },
    {
      key: 'username',
      header: 'Login Username',
      render: (u) => (
        <div style={{
          backgroundColor: isDarkMode ? '#111' : '#FFF3E0',
          padding: '4px 8px', borderRadius: t.radius.sm,
          display: 'inline-block', fontWeight: 'bold'
        }}>
          {u.username}
        </div>
      )
    },
    {
      key: 'access',
      header: 'Access Summary',
      render: (u) => {
        const values = Object.values(u.parsedPermissions || {});
        const full = values.filter((v) => v === 'Full Access').length;
        const read = values.filter((v) => v === 'Read Only').length;
        return (
          <div style={{ fontSize: t.fontSize.sm }}>
            <span style={{ color: t.income, fontWeight: 'bold' }}>{full} Full Access</span>
            {' | '}
            <span style={{ color: t.warning, fontWeight: 'bold' }}>{read} Read-Only</span>
          </div>
        );
      }
    }
  ];

  const columns: Array<Column<ParsedSystemUser>> = isDeletedView
    ? [
        ...baseColumns,
        { key: 'deletedAt', header: 'Disabled Date', render: (u) => <span style={{ fontSize: '12px', color: t.textFaint }}>{u.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', render: (u) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{u.deleteReason || '-'}</span> }
      ]
    : [
        ...baseColumns,
        {
          key: 'actions',
          header: 'Actions',
          align: 'right',
          render: (u) => (
            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onClick={() => handleEdit(u)}>Edit Access</Button>
              <button style={styles.deleteBtn} onClick={() => requestDisable(u)}>Disable</button>
            </div>
          )
        }
      ];

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="🔐"
        title="System Access Management"
        activeTab={viewMode}
        onTabChange={switchView}
        tabs={[
          { key: 'history', label: '📋 View Active Users' },
          { key: 'add', label: isEditing ? '✎ Editing User...' : '+ Create New User' },
          { key: 'roles', label: '🎭 Roles' },
          { key: 'deleted', label: '🗑️ Disabled Accounts', variant: 'danger' }
        ]}
      />

      {viewMode === 'roles' && <RolesManager t={t} notify={notify} onChanged={() => { loadRoles(); refetch(); }} />}

      {(viewMode === 'history' || isDeletedView) && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by name, username, or role..."
          />

          <TablePanel
            title={<>System User List</>}
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="users" />}
              columns={columns}
              rows={page.currentItems}
              rowKey="rawId"
              loading={loading}
              emptyMessage="No users found."
          />
        </>
      )}

      {viewMode === 'add' && (
        <FormLayout
          title={isEditing ? 'Edit User & Permissions' : 'Create User & Assign Access'}
          submitLabel={isEditing ? 'Update User Privileges' : 'Create Authorized User'}
          submitting={saving}
          onSubmit={handleSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          maxWidth="1000px"
        >
          <h4 style={{ margin: '0 0 15px 0', borderBottom: `2px solid ${t.border}`, paddingBottom: '8px', color: t.textMain }}>
            1. User Credentials
          </h4>
          <FormLayout.Grid columns={4} style={{ marginBottom: '30px' }}>
            <Input
              label="Full Name" name="name" required placeholder="John Doe"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
            />
            <Input
              label="Login Username" name="username" required placeholder="john.doe"
              value={userForm.username}
              onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
              style={{ backgroundColor: isDarkMode ? '#111' : '#FFF3E0', fontWeight: 'bold' }}
            />
            <Input
              label="Password" name="password" required={!isEditing}
              placeholder={isEditing ? 'Leave blank to keep current password' : 'Set Password'}
              value={userForm.password}
              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
            />
            <Select
              label="System Role"
              required
              value={userForm.roleId}
              onChange={(e) => chooseRole(e.target.value)}
              options={roleOptions}
              placeholder="Choose a role"
              hint={chosenRole?.description}
            />
          </FormLayout.Grid>
          {isEditing && chosenRole?.defaultPermissions && (
            <div style={{ margin: '-18px 0 24px' }}>
              <Button
                variant="ghost" size="sm" type="button"
                onClick={() => setPermissions(parsePermissions(chosenRole.defaultPermissions))}
              >
                Use {chosenRole.name}'s default permissions
              </Button>
            </div>
          )}

          <PermissionGrid title="2. Module Access Permissions" permissions={permissions} onChange={setPermissions} t={t} />
        </FormLayout>
      )}

      <PromptDialog
        open={Boolean(disablingUser)}
        title={`Disable ${disablingUser?.username || 'account'}`}
        message="Enter the reason for removing this user account. It will be kept in Disabled Accounts."
        placeholder="Reason for disabling"
        confirmLabel="Disable Account"
        requiredError="A reason is required to process this action."
        onSubmit={confirmDisable}
        onCancel={() => setDisablingUser(null)}
      />
    </div>
  );
};

export default UserAccessManagement;
